/**
 * Tile-level "Needs attention" ring helper for the per-tile chip
 * surface.
 *
 * Per `reference-rules-in-comments` (ai.knowledge/global). The chip
 * system design specifies a 1px yellow outline pulled 1px inside the
 * tile whenever `chipState !== null`. Sidebar preview tiles opt into
 * a rounded corner variant (`rounded-4`) via `{ rounded: true }`;
 * canvas tiles stay rect-cornered. In collapsed mode (small tray
 * tiles where the chip text would overflow) the ring is the SOLE
 * indicator — paired with a tooltip carrying the full chip label.
 *
 * Per `think-in-systems` (ai.knowledge/global): the ring is a CSS
 * concern applied to the parent tile's outer div, NOT a wrapping
 * component. Wrapping would force item.tsx to nest a new container,
 * which complicates the existing absolute-positioned badges. Returning
 * a className from a hook keeps the integration to a single line at
 * the call site:
 *
 *     <div className={twMerge('relative', className, useTileNeedsAttentionRingClass(chipState))}>
 *
 * The hook returns `''` when there is no chip state, so it composes
 * cleanly with `twMerge`.
 *
 * Returning a tooltip-aware Component for the collapsed-mode case is
 * intentionally out of scope here — `item.tsx` owns the placement
 * decision. This module exposes (a) the className helper and (b) a
 * thin tooltip component that callers can wrap the tile in when
 * collapsed.
 */
import type { ReactElement, ReactNode } from 'react';
import { Tooltip } from '@studio/design-system/tooltip';
import type { ChipState } from './chip-state.js';

/**
 * Return the Tailwind className applied to the parent tile when the
 * chip state is non-null. Returns the empty string when the tile is
 * healthy so callers can pass the result through `twMerge` without
 * branching.
 */
export const useTileNeedsAttentionRingClass = (
  state: ChipState | null,
  // Per Figma 2026-05-14: the sidebar preview tile carries a 4px
  // border-radius on its warning outline, while the main-canvas tile
  // is rectangular. Callers opt in via `{ rounded: true }`. Default
  // `false` keeps the canvas usage rect-cornered.
  options?: { rounded?: boolean }
): string => {
  // Tile-level needs-attention surface — Multi-Add Phase 2 (2026-05-14).
  // Earlier W8 attempts used `ring-2 ring-warning`, which fell through
  // to the focus-blue ring because the design-system's tailwind config
  // overrides `ringColor` to expose only `focus` + `DEFAULT` (see
  // `packages/design-system/src/tailwind/config.cjs::ringColor` ~line
  // 263). The fix is `outline-warning` instead — the same config does
  // NOT override `outlineColor`, so it picks up the theme-level
  // `colors.warning` token directly (~line 118). `outline-1`,
  // `-outline-offset-1`, and `rounded-4` are all in the design-
  // system's whitelisted scales (outlineWidth, outlineOffset,
  // borderRadius), so they generate CSS reliably. The negative offset
  // (-1px) pulls the outline INSIDE the tile so it doesn't bleed
  // across the gutter between sibling tiles. Modern Chromium honors
  // `border-radius` on `outline`, so the corners follow `rounded-4`
  // automatically when the caller opts into rounded mode.
  if (state === null) return '';
  return options?.rounded === true
    ? 'outline outline-1 outline-warning -outline-offset-1 rounded-4'
    : 'outline outline-1 outline-warning -outline-offset-1';
};

/**
 * Wrap a tile in a tooltip carrying the full chip label. Used in
 * collapsed mode where the chip text would overflow but the ring is
 * still visible. Collapsed-mode tooltip.
 *
 * Pass-through when `state` is null so the hover behavior is gated on
 * the chip being active.
 */
export interface TileNeedsAttentionRingProps {
  /** Pure-derived chip state. */
  state: ChipState | null;

  /** Subset of the device used for the tooltip text. */
  device: { name: string };

  /** The tile content (the chip-less original render). */
  children: ReactNode;
}

export const TileNeedsAttentionRingTooltip = ({
  state,
  device,
  children
}: TileNeedsAttentionRingProps): ReactElement => {
  if (state === null) {
    // No chip → no tooltip. Pass through. Cast keeps TS happy without
    // forcing the caller to branch.
    return (<>{children}</>) as ReactElement;
  }

  return (
    <Tooltip trigger={children}>
      {/* Plain text matches the chip's visible text; assistive tech
          reads the same string from both surfaces. */}
      <div className='text-[12px]'>
        {device.name} — {state.actionLink}
      </div>
    </Tooltip>
  );
};
